/**
 * Redux middleware that automatically tracks all dispatch calls for the reservation journey.
 * Tracks thunk actions (functions) and their execution time, including async work.
 *
 * This middleware must be placed AFTER redux-thunk middleware but BEFORE promiseMiddleware
 * to intercept promises after redux-thunk processes them but before promiseMiddleware transforms them.
 */

import { Middleware } from 'redux';
import { getCurrentJourneyTracker, type JourneyTracker } from './journeyTracker';

/**
 * Creates a Redux middleware that tracks all dispatch calls when journey tracking is active.
 * Automatically tracks:
 * - Thunk actions (functions) execution time
 * - Async work (promises returned by thunks)
 * - All nested dispatch calls within thunks
 *
 * This middleware must be placed AFTER redux-thunk middleware but BEFORE promiseMiddleware
 * to intercept promises after redux-thunk processes them but before promiseMiddleware transforms them.
 *
 * @param getTracker - Optional function to get the journey tracker instance.
 *                     If not provided, uses getCurrentJourneyTracker() to get the tracker from the Provider.
 */
export const createJourneyTrackingMiddleware = (getTracker?: () => JourneyTracker | null): Middleware => {
    return () => (next) => (action) => {
        const tracker = getTracker ? getTracker() : getCurrentJourneyTracker();

        // Only track if we have a tracker and it's active
        if (!tracker || !tracker.isActive()) {
            return next(action);
        }

        // Execute the action first (redux-thunk will have already processed function actions)
        // Nested dispatches from within thunk actions will also go through this middleware
        const result = next(action);

        // If the result is a promise (from a thunk action), track it
        if (result && typeof result.then === 'function') {
            // Try to get action name from various sources
            let actionName = 'thunk-action';

            if (typeof action === 'object' && action?.type) {
                // Regular action object with type
                actionName = action.type;
            } else if (typeof action === 'function') {
                // Thunk function - try to get its name
                actionName = action.name || 'anonymous-thunk';
            }

            // Track the promise from creation to resolution
            // Note: We track from when we intercept the promise, not from when it was created.
            // This means if the promise was created earlier, we might miss some execution time.
            // However, since we're placed right after redux-thunk, the promise should be fresh.
            const trackedPromise = tracker.trackAsync(result, actionName).catch((error: $TSFixMe) => {
                throw error;
            });

            // Ensure the promise is tracked even if it's not awaited
            // This is important for promises dispatched from thunk actions that aren't awaited
            void trackedPromise;

            return trackedPromise;
        }

        // For non-promise results, just return as-is
        return result;
    };
};
